import { useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// Phase 2Z.s-5 + V1.1 polish — Performance Focus import dialog.
// Two-step preview/commit; never writes until the manager confirms.
// V1.1 adds: header-row detection, smart separator detection
// (tab/comma/whitespace), UK DD/MM/YYYY hint, CSV template download,
// friendlier preview + commit wording, and an explicit Replace toggle.
// Employee matching: email → exact name → fuzzy (suggestion only).
// All imported rows land via the same kpi_values shape as manual
// entry, with source ∈ {paste, csv_import} + imported_batch_id.

// V1.1 — accepted header keywords (lowercased, snake_case form).
// Kept in lockstep with backend _IMP_HEADER_KEYWORDS.
export const _IMPORT_HEADER_KEYWORDS = new Set([
  "employee_name", "employee", "name", "employee_email", "email",
  "period_start", "start_date", "start", "from",
  "period_end", "end_date", "end", "to",
  "actual_value", "value", "actual", "result",
]);

// V1.1 — header detection. A row is a header iff ≥ 2 non-empty cells
// match the accepted header vocabulary and no cell looks like a value.
export function _importIsHeaderCells(cells) {
  if (!Array.isArray(cells)) return false;
  const norm = cells.map((c) => String(c || "").trim().toLowerCase()
    .replace(/\s+/g, "_"));
  const nonempty = norm.filter((c) => c.length > 0);
  if (nonempty.length < 2) return false;
  const matches = nonempty.filter((c) => _IMPORT_HEADER_KEYWORDS.has(c)).length;
  // Any header cell that's clearly a date or a number disqualifies as header.
  const looksLikeData = nonempty.some((c) =>
    /^\d{4}-\d{2}-\d{2}$/.test(c)
    || /^\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}$/.test(c)
    || /^-?\d+(\.\d+)?$/.test(c));
  if (looksLikeData) return false;
  return matches >= Math.max(2, nonempty.length - 1);
}

// V1.1 — pick a separator for a single line. Prefer tab (Excel/Sheets
// paste), else comma (CSV), else 2+ whitespace (rare hand-typed rows).
export function _importPickSeparator(line) {
  if (typeof line !== "string") return null;
  if (line.includes("\t")) return "\t";
  if (line.includes(",")) return ",";
  if (/\s{2,}/.test(line)) return /\s{2,}/;
  // Last resort: 4 simple words separated by single spaces — only
  // safe if exactly 4 tokens, because email + ISO dates contain no
  // spaces. Anything else is ambiguous and we leave the row to the
  // backend to reject with a friendly error.
  const tokens = line.trim().split(/\s+/);
  if (tokens.length === 4) return /\s+/;
  return null;
}

// Pure helper — split paste/CSV text into rows. Exported for locks.
// `kind` is accepted for backwards compatibility but the parser now
// auto-detects the separator per-line so Excel paste, CSV paste, and
// whitespace-separated rows all just work.
export function _importParseRows(text /* , kind */) {
  if (typeof text !== "string" || !text.trim()) return [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  const out = [];
  let headerSeen = false;
  for (const line of lines) {
    const sep = _importPickSeparator(line);
    let cells;
    if (sep === null) {
      // Unparseable — keep as a single-cell row so the backend can
      // surface a friendly error on it (rather than dropping silently).
      cells = [line.trim()];
    } else {
      cells = line.split(sep).map((c) => c.trim().replace(/^"|"$/g, ""));
    }
    // V1.1 — skip the first header row silently. We only skip ONE
    // header line so duplicate "header" data rows still surface as
    // errors. Allow header only as the very first non-blank line.
    if (!headerSeen && _importIsHeaderCells(cells)) {
      headerSeen = true;
      continue;
    }
    headerSeen = true;
    out.push({
      employee_ref: cells[0] || "",
      period_start: cells[1] || "",
      period_end: cells[2] || "",
      value: cells[3] || "",
    });
  }
  return out;
}

// V1.1 — CSV template content. Pure for tests.
export function _importCsvTemplateText() {
  return (
    "employee_name,period_start,period_end,actual_value\n"
    + "John Doe,2026-06-29,2026-07-03,55\n"
  );
}

function _downloadCsvTemplate() {
  const text = _importCsvTemplateText();
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "performance-results-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ImportResultsDialog({
  open, onOpenChange, activity, employees, onCommitted,
}) {
  const [kind, setKind] = useState("paste");
  const [pasteText, setPasteText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [warningEmployee, setWarningEmployee] = useState({});  // row_index -> employee_id
  const [replaceExisting, setReplaceExisting] = useState(false);

  // V1.1 — derive commit-time totals so the button + summary line
  // tell the manager exactly what will happen.
  const commitPlan = useMemo(() => {
    if (!preview) return null;
    const ready = preview.valid_rows.length;
    const willSkip = preview.warning_rows.filter((r) =>
      r.action !== "needs_employee" && !replaceExisting).length;
    const willReplace = preview.warning_rows.filter((r) =>
      r.action !== "needs_employee" && replaceExisting).length;
    const needsEmployee = preview.warning_rows.filter((r) =>
      r.action === "needs_employee" && warningEmployee[r.row_index]).length;
    const totalToImport = ready + willReplace + needsEmployee;
    return { ready, willSkip, willReplace, needsEmployee, totalToImport };
  }, [preview, warningEmployee, replaceExisting]);

  const runPreview = async () => {
    if (!activity) return;
    setBusy(true);
    try {
      const rows = _importParseRows(pasteText, kind);
      if (rows.length === 0) {
        toast.error("Paste at least one row");
        setBusy(false); return;
      }
      const { data } = await api.post(
        `/kpis/${activity.kpi_id}/import/preview`,
        { kind, rows });
      setPreview(data);
      setWarningEmployee({});
      setReplaceExisting(false);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Preview failed");
    } finally { setBusy(false); }
  };

  const runCommit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const out = [];
      preview.valid_rows.forEach((r) => out.push({
        employee_id: r.match.employee_id,
        period_start: r.period_start, period_end: r.period_end,
        value: r.value, action: "insert",
      }));
      preview.warning_rows.forEach((r) => {
        // Imported only if manager picked an employee OR the row had
        // an existing-overlap (which is fine to send through — backend
        // will skip or replace based on import_strategy).
        const eid = warningEmployee[r.row_index] || r.match?.employee_id;
        if (!eid) return;
        out.push({
          employee_id: eid,
          period_start: r.period_start, period_end: r.period_end,
          value: r.value, action: "insert",
        });
      });
      if (out.length === 0) {
        toast.error("No rows ready to import");
        setBusy(false); return;
      }
      const { data } = await api.post(
        `/kpis/${activity.kpi_id}/import/commit`,
        { kind: preview.kind, batch_id: preview.batch_id,
          // V1.1 — explicit strategy. Replace is opt-in via a clearly
          // labelled checkbox; manual entries are still protected by
          // confirm_overwrite_manual=false (server enforces).
          import_strategy: replaceExisting ? "replace" : "skip_existing",
          confirm_overwrite_manual: false,
          rows: out });
      // V1.1 — friendlier commit toast.
      const ins = data.inserted || 0;
      const rep = data.replaced || 0;
      const skp = data.skipped || 0;
      if (ins === 0 && rep === 0 && skp > 0) {
        toast.success(
          "All rows already exist for that period — nothing was imported."
          + " Tick \"Replace existing\" before importing to overwrite."
        );
      } else {
        const parts = [];
        if (ins > 0) parts.push(`${ins} new`);
        if (rep > 0) parts.push(`${rep} replaced`);
        if (skp > 0) parts.push(`${skp} skipped (already existed)`);
        toast.success("Import complete — " + parts.join(", ") + ".");
      }
      onCommitted?.();
      onOpenChange(false);
      setPreview(null); setPasteText("");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setPasteText(text); setKind("csv_import");
  };

  // Phase 2Z.ad - Option B - cheap "feels like a spreadsheet" polish.
  // Live counter of non-blank rows the manager has pasted. Pure read
  // of pasteText; never touches the parser, never touches the import
  // engine. Drives both the counter UI and the row-number gutter.
  const pastedRowCount = useMemo(() => {
    if (!pasteText) return 0;
    return pasteText.split(/\r?\n/).filter((l) => l.trim().length > 0).length;
  }, [pasteText]);
  // Render at least 8 gutter numbers so the empty state still looks
  // like a spreadsheet, then grow with the paste.
  const gutterRows = Math.max(8, pastedRowCount + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        fullscreenOnMobile
        data-testid="performance-import-dialog"
      >
        <DialogHeader>
          <DialogTitle>Import results — {activity?.name || ""}</DialogTitle>
          <DialogDescription>
            Paste rows from Excel/Google Sheets or upload a CSV.
            Headers are auto-detected and skipped.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Button
                variant={kind === "paste" ? "default" : "outline"}
                size="sm"
                onClick={() => setKind("paste")}
                data-testid="import-tab-paste"
              >Paste</Button>
              <Button
                variant={kind === "csv_import" ? "default" : "outline"}
                size="sm"
                onClick={() => setKind("csv_import")}
                data-testid="import-tab-csv"
              >CSV file</Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={_downloadCsvTemplate}
                className="ml-auto"
                data-testid="import-csv-template-btn"
              >Download CSV template</Button>
            </div>

            {/* V1.1 — clear example so customers can't mis-shape a row. */}
            <div
              className="rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-xs"
              data-testid="import-example-block"
            >
              <div className="font-medium text-[#0F1B3D] mb-1">
                Accepted columns
              </div>
              <div className="text-[#4B5773]">
                <code>employee_name</code> (or email),{" "}
                <code>period_start</code>, <code>period_end</code>,{" "}
                <code>actual_value</code>
              </div>
              <div className="font-medium text-[#0F1B3D] mt-2 mb-1">
                Example
              </div>
              <pre className="text-[11px] font-mono whitespace-pre text-[#0F1B3D]">{`employee_name,period_start,period_end,actual_value
John Doe,2026-06-29,2026-07-03,55`}</pre>
              <div className="text-[#4B5773] mt-2">
                Dates: <b>YYYY-MM-DD</b> (e.g. 2026-07-01) or UK{" "}
                <b>DD/MM/YYYY</b> (e.g. 01/07/2026). Tab, comma or simple
                spaced rows are all accepted.
              </div>
            </div>

            {kind === "csv_import" && (
              <Input type="file" accept=".csv,text/csv"
                onChange={handleFile}
                data-testid="import-csv-file" />
            )}
            <Label className="text-xs text-[#4B5773]">
              Rows (one per line):
            </Label>

            {/* Phase 2Z.ad - Option B - spreadsheet-shell wrapper.
                Header strip + row-number gutter + grid-line texture
                make the paste area feel like Excel without changing
                paste behaviour. The textarea is the same control as
                before so paste, undo, selection and CSV upload all
                continue to work identically. */}
            <div
              className="rounded-lg border border-[#D8E1F0] bg-white overflow-hidden shadow-sm"
              data-testid="import-paste-spreadsheet"
            >
              <div
                className="grid grid-cols-[3rem_1fr_8rem_8rem_6rem] bg-[#EEF3FB] text-[10px] uppercase tracking-widest text-[#4B5773] font-semibold"
                data-testid="import-paste-header-strip"
              >
                <div className="px-2 py-2 border-r border-[#D8E1F0] text-center">#</div>
                <div className="px-2 py-2 border-r border-[#D8E1F0]">Employee</div>
                <div className="px-2 py-2 border-r border-[#D8E1F0]">From</div>
                <div className="px-2 py-2 border-r border-[#D8E1F0]">To</div>
                <div className="px-2 py-2 text-right">Value</div>
              </div>
              <div className="flex" style={{ minHeight: "200px" }}>
                <div
                  className="w-12 shrink-0 border-r border-[#E5EAF3] bg-[#F6F9FE] py-2 text-right pr-2 font-mono text-[11px] text-[#9CA6BD] select-none"
                  data-testid="import-paste-gutter"
                  style={{ lineHeight: "1.45em" }}
                >
                  {Array.from({ length: gutterRows }, (_, i) => (
                    <div key={i}>{i + 1}</div>
                  ))}
                </div>
                <textarea
                  data-testid="import-paste-textarea"
                  className="flex-1 px-2 py-2 text-sm font-mono outline-none resize-y border-0 focus:ring-0 bg-white"
                  placeholder={kind === "csv_import"
                    ? "employee_name,period_start,period_end,actual_value\nJohn Doe,2026-06-29,2026-07-03,55"
                    : "John Doe\t2026-06-29\t2026-07-03\t55"}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  style={{
                    lineHeight: "1.45em",
                    minHeight: "200px",
                    backgroundImage:
                      "repeating-linear-gradient(to bottom, transparent 0, transparent calc(1.45em - 1px), #F1F2F6 calc(1.45em - 1px), #F1F2F6 1.45em)",
                  }}
                />
              </div>
            </div>
            <div
              className="text-[11px] text-[#4B5773] flex items-center justify-between"
              data-testid="import-paste-counter"
            >
              <span>
                {pastedRowCount === 0
                  ? "Paste rows from Excel or Google Sheets, or type one per line."
                  : `${pastedRowCount} row${pastedRowCount === 1 ? "" : "s"} pasted.`}
              </span>
              <span className="text-[#9CA6BD]">Header row is auto-detected and skipped.</span>
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {/* V1.1 — plain-English summary line. */}
            <div
              className="text-xs text-[#0F1B3D]"
              data-testid="import-preview-summary"
            >
              <b>{preview.summary.valid}</b> ready to import.{" "}
              <b>{preview.summary.warnings}</b> need attention.{" "}
              <b>{preview.summary.errors}</b> have errors and will be skipped.
              {preview.summary.headers_skipped > 0 && (
                <span className="text-[#4B5773]">
                  {" "}({preview.summary.headers_skipped} header row{
                    preview.summary.headers_skipped === 1 ? "" : "s"} skipped.)
                </span>
              )}
            </div>

            {/* V1.1 — explicit Replace toggle, default OFF.
                Manual entries are always protected on the server. */}
            <label
              className="flex items-start gap-2 text-xs text-[#0F1B3D] bg-[#FFF7ED] border border-[#FDBA74] rounded-md px-3 py-2 cursor-pointer"
              data-testid="import-replace-toggle-label"
            >
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                data-testid="import-replace-toggle"
                className="mt-0.5"
              />
              <span>
                <b>Replace existing imported rows</b> for the same employee and
                period. Manually entered rows are never overwritten — they will
                always be skipped.
              </span>
            </label>

            <div className="rounded-lg border border-[#D8E1F0] bg-white overflow-hidden">
            <table className="w-full text-xs" data-testid="import-preview-table">
              <thead className="bg-[#EEF3FB] sticky top-0 z-10">
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#4B5773] font-semibold">
                  <th className="px-2 py-2 border-b border-[#D8E1F0] text-center w-10">#</th>
                  <th className="px-2 py-2 border-b border-[#D8E1F0]">Employee</th>
                  <th className="px-2 py-2 border-b border-[#D8E1F0] w-24">From</th>
                  <th className="px-2 py-2 border-b border-[#D8E1F0] w-24">To</th>
                  <th className="px-2 py-2 border-b border-[#D8E1F0] w-20 text-right">Value</th>
                  <th className="px-2 py-2 border-b border-[#D8E1F0] w-24 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.valid_rows.map((r, i) => (
                  <tr key={`v${r.row_index}`}
                      className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFD]"}>
                    <td className="px-2 py-1.5 text-center font-mono text-[#9CA6BD]">{r.row_index + 1}</td>
                    <td className="px-2 py-1.5">{r.match.name}</td>
                    <td className="px-2 py-1.5 font-mono">{r.period_start}</td>
                    <td className="px-2 py-1.5 font-mono">{r.period_end}</td>
                    <td className="px-2 py-1.5 font-mono text-right tabular-nums">{r.value}</td>
                    <td className="px-2 py-1.5 text-right">
                      <span className="inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Ready</span>
                    </td>
                  </tr>
                ))}
                {preview.warning_rows.map((r, i) => (
                  <tr key={`w${r.row_index}`}
                      className="border-t border-[#FDE7C9] bg-[#FFF7ED]">
                    <td className="px-2 py-1.5 text-center font-mono text-[#9CA6BD]">{r.row_index + 1}</td>
                    <td className="px-2 py-1.5">
                      {r.action === "needs_employee" ? (
                        <select
                          data-testid={`import-warning-employee-${r.row_index}`}
                          className="border rounded px-1 py-0.5 max-w-full"
                          value={warningEmployee[r.row_index] || ""}
                          onChange={(e) => setWarningEmployee((s) => ({
                            ...s, [r.row_index]: e.target.value,
                          }))}
                        >
                          <option value="">
                            {r.match?.status === "fuzzy" && r.match?.suggestion
                              ? `Pick employee (suggested: ${r.match.suggestion.name})`
                              : `Pick employee for "${r.employee_ref}"`}
                          </option>
                          {r.match?.status === "fuzzy" && r.match?.suggestion && (
                            <option value={r.match.suggestion.employee_id}>
                              {r.match.suggestion.name} (suggested)
                            </option>
                          )}
                          {employees.map((emp) => (
                            <option key={emp.employee_id} value={emp.employee_id}>
                              {emp.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>{r.match?.name || r.employee_ref}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono">{r.period_start}</td>
                    <td className="px-2 py-1.5 font-mono">{r.period_end}</td>
                    <td className="px-2 py-1.5 font-mono text-right tabular-nums">{r.value}</td>
                    <td
                      className="px-2 py-1.5 text-right"
                      data-testid={`import-warning-reason-${r.row_index}`}
                    >
                      <span className="inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                            title={r.action === "needs_employee"
                              ? "Needs employee"
                              : replaceExisting
                                ? "Existing result will be replaced"
                                : "Existing result found for this employee, activity and period. This row will be skipped unless you tick Replace existing."}>
                        {r.action === "needs_employee" ? "Pick employee" : (replaceExisting ? "Will replace" : "Exists")}
                      </span>
                    </td>
                  </tr>
                ))}
                {preview.error_rows.map((r) => (
                  <tr key={`e${r.row_index}`} className="border-t border-[#FECACA] bg-[#FEF2F2]">
                    <td className="px-2 py-1.5 text-center font-mono text-[#9CA6BD]">{r.row_index + 1}</td>
                    <td colSpan={4} className="px-2 py-1.5 text-[#B91C1C]">
                      {r.employee_ref || "(empty)"}: {r.errors.join("; ")}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <span className="inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">Skipped</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* V1.1 — plain-English summary of what Import will do. */}
            {commitPlan && (
              <div
                className="text-xs text-[#4B5773] border-t border-[#F1F2F6] pt-2"
                data-testid="import-commit-plan"
              >
                On Import:{" "}
                <b>{commitPlan.totalToImport}</b> row{commitPlan.totalToImport === 1 ? "" : "s"} will be saved
                ({commitPlan.ready} new
                {commitPlan.willReplace > 0 && `, ${commitPlan.willReplace} replaced`}
                {commitPlan.needsEmployee > 0 && `, ${commitPlan.needsEmployee} resolved manually`}).{" "}
                {commitPlan.willSkip > 0 && (
                  <span>
                    <b>{commitPlan.willSkip}</b> will be skipped (already exist).
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!preview ? (
            <Button onClick={runPreview} disabled={busy}
              data-testid="import-preview-btn">
              {busy ? "Previewing…" : "Preview"}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreview(null)}
                data-testid="import-back-btn">Back</Button>
              <Button onClick={runCommit}
                disabled={busy || (commitPlan && commitPlan.totalToImport === 0)}
                data-testid="import-commit-btn">
                {busy
                  ? "Importing…"
                  : commitPlan && commitPlan.totalToImport === 0
                    ? "Nothing to import"
                    : replaceExisting
                      ? `Import & replace ${commitPlan?.totalToImport || 0} row${(commitPlan?.totalToImport || 0) === 1 ? "" : "s"}`
                      : `Import ${commitPlan?.totalToImport || 0} row${(commitPlan?.totalToImport || 0) === 1 ? "" : "s"}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
